
import React from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import BackToTopButton from '../../components/base/BackToTopButton';
import CookieBanner from '../../components/base/CookieBanner';
import SEOHead from '../../components/seo/SEOHead';

export default function Servicios() {
  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Servicios de Pintura Profesional",
    "description": "Servicios completos de pintura para particulares y empresas en Olot, Girona y la Garrotxa",
    "provider": {
      "@type": "LocalBusiness",
      "name": "PintoLot",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Olot",
        "addressRegion": "Girona",
        "addressCountry": "ES"
      },
      "telephone": "+34609936590"
    },
    "areaServed": ["Olot", "Girona", "La Garrotxa"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Pintura",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Interior",
            "description": "Pintura profesional para interiores de viviendas y locales"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Exterior",
            "description": "Pintura de fachadas y exteriores resistente al clima"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Comercial",
            "description": "Servicios de pintura para oficinas y espacios comerciales"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Lacados y Barnizados",
            "description": "Acabados especializados para maderas y metales"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="Servicios de Pintura Profesional en Olot y Garrotxa | PintoLot"
        description="Servicios completos de pintura para particulares y empresas en Olot, Girona y Garrotxa. Pintura interior, exterior, comercial e industrial. Presupuesto gratuito."
        keywords="servicios pintura Olot, pintura interior Girona, pintura exterior Garrotxa, pintura comercial, lacados barnizados"
        canonical={`${import.meta.env.VITE_SITE_URL}/servicios`}
        ogTitle="Servicios de Pintura Profesional - PintoLot Olot"
        ogDescription="Descubre todos nuestros servicios de pintura: interior, exterior, comercial e industrial. Calidad profesional garantizada en Olot y Garrotxa."
        schema={seoSchema}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Pintor profesional en Olot y la Garrotxa
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos servicios completos de pintura para particulares y empresas en Girona y provincia. Calidad profesional garantizada en cada proyecto.
            </p>
          </div>
        </section>

        {/* Residential Services */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Servicios para particulares
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transformamos tu hogar con servicios de pintura profesional. Desde habitaciones individuales hasta proyectos completos de vivienda.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Pintura interior
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Renovamos el interior de tu hogar con acabados perfectos y duraderos. Utilizamos pinturas de alta calidad adaptadas a cada estancia.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Dormitorios y salones</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Cocinas y baños</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Pasillos y escaleras</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Techos y molduras</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c8e18511d43d015552026035dd40c41b.jpg"
                  alt="Pintura interior profesional en Olot"
                  className="rounded-lg shadow-xl object-cover w-full h-80"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=house%20exterior%20facade%20being%20painted%2C%20professional%20painters%20working%20on%20residential%20building%20walls%2C%20ladder%20and%20painting%20equipment%2C%20clean%20work%20environment%2C%20bright%20daylight%20photography&width=600&height=400&seq=exterior-painting&orientation=landscape"
                  alt="Pintura exterior profesional en Girona"
                  className="rounded-lg shadow-xl object-cover w-full h-80"
                />
              </div>

              <div className="order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Pintura exterior
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Protegemos y embellecemos el exterior de tu vivienda con pinturas resistentes a las condiciones climáticas de la zona de Girona.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Fachadas y muros exteriores</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Ventanas y puertas</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Verjas y elementos metálicos</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Tratamientos impermeabilizantes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Servicios para empresas
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Servicios profesionales de pintura para todo tipo de espacios comerciales e industriales en Olot, Girona y toda la comarca de la Garrotxa.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-building-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-center mb-4">Oficinas y despachos</h3>
                <p className="text-gray-600 text-center mb-6">
                  Creamos ambientes de trabajo profesionales y acogedores que reflejan la imagen de tu empresa.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Espacios de trabajo modernos</li>
                  <li>• Salas de reuniones</li>
                  <li>• Zonas de recepción</li>
                  <li>• Áreas comunes</li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-store-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-center mb-4">Locales comerciales</h3>
                <p className="text-gray-600 text-center mb-6">
                  Ayudamos a crear espacios atractivos que potencien la experiencia de tus clientes.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Tiendas y boutiques</li>
                  <li>• Restaurantes y bares</li>
                  <li>• Centros comerciales</li>
                  <li>• Espacios de ocio</li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-center mb-4">Naves industriales</h3>
                <p className="text-gray-600 text-center mb-6">
                  Soluciones específicas para espacios industriales con pinturas resistentes y duraderas.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Naves de producción</li>
                  <li>• Almacenes y depósitos</li>
                  <li>• Talleres mecánicos</li>
                  <li>• Instalaciones técnicas</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Specialized Services */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Servicios especializados
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Técnicas especializadas y tratamientos específicos para proyectos únicos que requieren conocimientos avanzados y materiales especiales.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Lacados y barnizados
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Acabados de alta calidad para maderas, metales y otros materiales. Técnicas especializadas que garantizan durabilidad y belleza.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Lacado de puertas y ventanas</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Barnizado de muebles</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Restauración de superficies</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Acabados especiales</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20lacquering%20and%20varnishing%20work%2C%20wooden%20doors%20and%20furniture%20being%20finished%20with%20glossy%20lacquer%2C%20workshop%20environment%2C%20detailed%20craftsmanship%2C%20professional%20tools%20and%20brushes&width=600&height=400&seq=lacquering-work&orientation=landscape"
                  alt="Lacados y barnizados profesionales"
                  className="rounded-lg shadow-xl object-cover w-full h-80"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=decorative%20painting%20techniques%2C%20textured%20wall%20finishes%2C%20artistic%20wall%20treatments%2C%20professional%20decorator%20applying%20special%20paint%20effects%2C%20modern%20interior%20design%20elements&width=600&height=400&seq=decorative-techniques&orientation=landscape"
                  alt="Técnicas decorativas especiales"
                  className="rounded-lg shadow-xl object-cover w-full h-80"
                />
              </div>

              <div className="order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Técnicas decorativas
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Efectos decorativos únicos que aportan personalidad y estilo a tus espacios. Técnicas artísticas aplicadas con maestría profesional.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Estuco y texturas</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Efectos de envejecido</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Murales decorativos</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Acabados metalizados</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nuestro proceso de trabajo
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Seguimos un proceso estructurado y profesional para garantizar los mejores resultados en cada proyecto de pintura.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Consulta inicial</h3>
                <p className="text-gray-600">
                  Evaluamos tu proyecto, necesidades específicas y proporcionamos asesoramiento profesional personalizado.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Presupuesto detallado</h3>
                <p className="text-gray-600">
                  Preparamos un presupuesto completo y transparente, incluyendo materiales, mano de obra y plazos de ejecución.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Ejecución profesional</h3>
                <p className="text-gray-600">
                  Realizamos el trabajo con la máxima calidad, cumpliendo los plazos acordados y manteniendo la limpieza del espacio.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Entrega y garantía</h3>
                <p className="text-gray-600">
                  Entregamos el proyecto terminado con la máxima calidad y proporcionamos garantía completa de nuestro trabajo.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Materials */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Materiales de primera calidad
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Trabajamos exclusivamente con las mejores marcas de pintura y materiales del mercado para garantizar resultados duraderos y acabados perfectos.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-drop-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Pinturas Bruguer</h3>
                <p className="text-gray-600 text-sm">
                  Líder en innovación y calidad, perfectas para cualquier proyecto interior y exterior.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-brush-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Pinturas Jotun</h3>
                <p className="text-gray-600 text-sm">
                  Especialistas en protección y decoración con tecnología avanzada y larga durabilidad.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-palette-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Pinturas Titan</h3>
                <p className="text-gray-600 text-sm">
                  Soluciones completas para profesionales con amplia gama de productos especializados.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-paint-brush-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Sherwin Williams</h3>
                <p className="text-gray-600 text-sm">
                  Tecnología americana de vanguardia para los proyectos más exigentes y especializados.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-[#51b038]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Necesitas alguno de nuestros servicios?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros para recibir asesoramiento personalizado y un presupuesto gratuito para tu proyecto en Olot y la Garrotxa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contacto" 
                className="bg-white text-[#51b038] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar presupuesto
              </a>
              <a 
                href="tel:609936590" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#51b038] px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar ahora
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTopButton />
      <CookieBanner />
    </>
  );
}
